import React, { useRef, useState } from "react";
import { Box, Text, Button, HStack, Flex } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { HeadingText } from "../common/text/HeadingText";
import { useTranslation } from "react-i18next";
import { getPdfPageCount } from "@root/utils/pdf-page-counter";
import { InputBox, UploadIconButton } from "@root/components/dashboard/style";
import PermitUploadIcon from "@root/assets/icons/permitUploadIcon";
import GreenBoxIcon from "@root/assets/icons/greenfileicon";
import { HelpPopover } from "@root/components/common/help-popover";
import { ContractUploadHelpContent } from "./ContractUploadHelpContent";
import { DrawingUploadHelpContent } from "./DrawingUploadHelpContent";

interface UploadAreaProps {
  title: string;
  handleFiles: (file: File, cb: (done: boolean) => void) => void;
  progress?: number;
  isUploading?: boolean;
  onCancelUpload?: () => void;
  // Optional PDF validation props
  validatePdfPages?: boolean;
  maxPdfPages?: number;
  onPdfValidationError?: (message: string) => void;
  // Optional file size validation
  maxFileSizeMB?: number;
  onFileSizeError?: (message: string) => void;
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  title,
  handleFiles,
  progress = 0,
  isUploading = false,
  onCancelUpload,
  validatePdfPages = false,
  onPdfValidationError,
  onFileSizeError,
}) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const maxPdfPages = Number(process.env.REACT_APP_FILE_PAGES_LIMIT) || 50;
  const maxFileSizeMB =
    Number(process.env.REACT_APP_MATERIAL_FILE_LIMIT) || 500;

  const [uploadedFile, setUploadedFile] = useState<{
    name: string;
    success: boolean;
  } | null>(null);

  const processFile = async (file: File) => {
    // Check file size if maxFileSizeMB is set
    if (maxFileSizeMB) {
      const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        const message =
          t("upload_document.fileTooLarge", {
            maxSize: maxFileSizeMB,
          }) || `File size exceeds ${maxFileSizeMB}MB limit`;
        onFileSizeError?.(message);
        return; // Skip this file and don't upload
      }
    }

    // Check if PDF validation is enabled and file is a PDF
    if (validatePdfPages && file.type === "application/pdf") {
      try {
        const pageCount = await getPdfPageCount(file);
        if (pageCount > maxPdfPages) {
          const message = t("upload_document.maxPagesExceeded", {
            maxPages: maxPdfPages,
          });
          onPdfValidationError?.(message);
          return; // Skip this file and don't upload
        }
      } catch (error) {
        console.error("PDF validation error:", error);
        const message = t("upload_document.pdfValidationFailed");
        onPdfValidationError?.(message);
        return; // Skip this file and don't upload
      }
    }

    // If validation passes (or is disabled), proceed with upload
    handleFiles(file, (done: boolean) => {
      console.log(`${file?.name} upload done?`, done);
      setUploadedFile({ name: file?.name, success: done });
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      for (const file of Array.from(e.target.files)) {
        await processFile(file);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (isUploading) return;

    if (e.dataTransfer.files?.length) {
      for (const file of Array.from(e.dataTransfer.files)) {
        await processFile(file);
      }
    }
  };

  const handleClick = () => {
    if (isUploading) return;
    inputRef.current?.click(); // trigger hidden input
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancelUpload?.();
    setUploadedFile(null);
  };

  // Check if this is for material comparison tour guide
  const isContractUpload =
    title?.includes("Contract") ||
    title === t("materialCamparison.uploadContract");
  const isDrawingUpload =
    title?.includes("Drawing") ||
    title === t("materialCamparison.uploadDrawing");

  return (
    <Box
      // padding={"15px"}
      // borderRadius={"8px"}
      // border={"1px solid #CDD6DC99"}
      bg={"#fff"}
      data-tour-item={
        isContractUpload
          ? "material-contract-upload"
          : isDrawingUpload
          ? "material-drawing-upload"
          : undefined
      }
    >
      <Flex gap={"10px"} alignItems={"center"} mb={3}>
        <HeadingText color="#15161A" text={title} />
        {isContractUpload && (
          <HelpPopover>
            <ContractUploadHelpContent />
          </HelpPopover>
        )}
        {isDrawingUpload && (
          <HelpPopover>
            <DrawingUploadHelpContent />
          </HelpPopover>
        )}
      </Flex>

      <Box
        shadow={"0px 4px 12px 0px #D0CDE142"}
        borderRadius={"18px"}
        padding={"5px"}
        position="relative"
        sx={{
          background: `
            url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg'%3e%3crect x='1' y='1' width='99%25' height='99%25' fill='none' rx='17' ry='17' stroke='%23349AE2' stroke-width='1.3' stroke-dasharray='8 10' stroke-linecap='round'/%3e%3c/svg%3e"),
            ${isUploading ? "#E8F5E9" : "#349AE20A"}
          `,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
        }}
      >
        <InputBox
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={(e: any) => e.preventDefault()}
          cursor={isUploading ? "default" : "pointer"}
          aria-disabled={isUploading}
          bg={"transparent"}
          border="none"
        >
          {/* hidden input */}
          <input
            type="file"
            accept=".pdf"
            multiple
            ref={inputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {isUploading || uploadedFile?.success ? (
            <Box
              position="relative"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              width="80px"
              height="80px"
            >
              {/* Circular Progress */}
              {isUploading && (
                <Box
                  position="absolute"
                  width="80px"
                  height="80px"
                  top="0"
                  left="0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="80"
                    height="80"
                    style={{ transform: "rotate(-90deg)" }}
                  >
                    {/* Background circle */}
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      stroke="#E2E8F0"
                      strokeWidth="4"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      stroke="#71B48D"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 36}`}
                      strokeDashoffset={`${
                        2 * Math.PI * 36 * (1 - progress / 100)
                      }`}
                      style={{
                        transition: "stroke-dashoffset 0.3s ease",
                      }}
                    />
                  </svg>
                </Box>
              )}
              <Box
                position="relative"
                zIndex={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <GreenBoxIcon />
              </Box>
            </Box>
          ) : (
            <UploadIconButton
              aria-label="upload"
              icon={<PermitUploadIcon />}
              isRound
            />
          )}

          {!uploadedFile?.success && !isUploading && (
            <HStack spacing={2}>
              <Text fontWeight="500" fontSize="lg" color="#2F2F34">
                {t("materialCamparison.dropFile")}
              </Text>
            </HStack>
          )}

          {uploadedFile?.success && !isUploading && (
            <Text fontWeight="500" fontSize="lg" color="#2F2F34" mt={4}>
              {uploadedFile?.name}
            </Text>
          )}

          {/* Progress Text and Cancel Button */}
          {isUploading && (
            <>
              <Text fontSize="sm" color="gray.700" mt={3}>
                {progress < 100
                  ? t("upload_document.processing_progress", { progress })
                  : t("upload_document.completed")}
              </Text>
              <Box mt={3} onClick={(e) => e.stopPropagation()}>
                <Button
                  onClick={handleCancelClick}
                  variant="outline"
                  colorScheme="red"
                  rightIcon={<CloseIcon boxSize="3" />}
                  size="sm"
                  isDisabled={!isUploading}
                >
                  {t("upload_document.cancelUploading")}
                </Button>
              </Box>
            </>
          )}

          {!isUploading && !uploadedFile?.success && (
            <Text marginTop={"10px"}>
              <Box as="span" color="#5C6670">
                {t("upload_document.supports")}:
              </Box>
              <Box as="span" fontWeight={500} color={"#2F2F34"} ml={"5px"}>
                {t("upload_document.pdf")}
                {maxFileSizeMB && ` • ${maxFileSizeMB}MB`}
              </Box>
            </Text>
          )}
        </InputBox>
      </Box>
    </Box>
  );
};
